import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { LiveGenreIdSchema, LiveId, LiveIdSchema, SpaceKeySchema, UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { LatestLiveCount, LiveTags } from "./live"

export const LivePointGrantStatus = z.enum(["pending", "granted", "skipped"])

export type LivePointGrantStatus = z.infer<typeof LivePointGrantStatus>

export const LiveHistoryDocumentBody = z.object({
  user_id: UserIdSchema.describe("配信者（スペースオーナー）のユーザーID。"),
  space_key: SpaceKeySchema.describe("ライブが行われたスペースのキー。"),
  users: z.array(UserIdSchema).describe("このライブ中にスペースで online になったユーザーIDの一覧。"),
  genre_id: LiveGenreIdSchema.describe("ライブのジャンルID。"),
  tags: LiveTags,
  latest_count: LatestLiveCount.describe("ライブ終了時点の視聴者数情報。"),
  started_at: z.number().describe("ライブの開始時刻（live.created_at のコピー）。"),
  stopped_at: z.number().describe("ライブの終了時刻。"),
  live_point_grant_status: LivePointGrantStatus.describe("LIVE ポイント付与ステータス。CronJob が pending のものを検出して grant-live-points タスクを投入する。"),
  live_point_conversion_blocked: z.boolean().describe("true の場合 LIVE ポイント変換をスキップする（管理者がブロック設定した場合など）。"),
  granted_live_point_at: z.number().nullable().describe("LIVE ポイント付与が完了した日時（Unix timestamp ms）。未付与の場合は null。"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type LiveHistoryDocumentBody = z.infer<typeof LiveHistoryDocumentBody>

export const LiveHistoryDocument = z.intersection(
  LiveHistoryDocumentBody,
  z.object({ id: LiveIdSchema }),
)

export type LiveHistoryDocument = z.infer<typeof LiveHistoryDocument>

/**
 * 管理者による LIVEポイント変換ブロック/解除リクエスト（LIVE単位）。
 */
export const UpdateLivePointConversionBlockRequest = z.object({
  blocked: z.boolean().describe("true でブロック、false で解除"),
  reason: z.string().min(1).describe("ブロック/解除理由（必須）"),
})

export type UpdateLivePointConversionBlockRequest = z.infer<typeof UpdateLivePointConversionBlockRequest>

export class LiveHistoryDocuments {

  static toDocumentData(doc: LiveHistoryDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LiveHistoryDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LiveId(doc.id),
      user_id: data["user_id"],
      space_key: data["space_key"],
      users: data["users"],
      genre_id: data["genre_id"],
      tags: data["tags"],
      latest_count: data["latest_count"] == null ? {
        current: 0,
        overall: 0,
      } : {
        current: data["latest_count"]["current"],
        overall: data["latest_count"]["overall"],
        power: data["latest_count"]["power"],
        current_max: data["latest_count"]["current_max"],
      },
      started_at: data["started_at"],
      stopped_at: data["stopped_at"],
      live_point_grant_status: data["live_point_grant_status"] ?? "pending",
      live_point_conversion_blocked: data["live_point_conversion_blocked"] ?? false,
      granted_live_point_at: data["granted_live_point_at"] ?? null,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
